import React, { useEffect, useState } from 'react';
import { WizardStepWrapper } from '../step';
import {
  H2,
  IndicatesRequired,
  RadioCardGroup,
  RadioCardOption,
  RadioCardOptionOrientation,
  RadioCardSizing,
  StackedColumn,
  StackedColumnSpacing,
} from '../../../../core';
import { LocationDetails, SchedulingDetails } from '../../../../api/models';
import { getLocationOptionsFromAppointmentType } from '../../../../utils';

export interface LocationStepPresenterProps {
  schedulingDetails: SchedulingDetails;
  onContinue: (locationDetails: LocationDetails) => void;
}

const findLocationDetails = (
  locationOptions: RadioCardOption[],
  locationId: string
): LocationDetails | undefined => {
  const locationOption = locationOptions.find(
    (locationOption) => locationOption.value === locationId
  );
  if (locationOption) {
    return {
      searchLocationId: locationId,
      searchLocationName: locationOption.label,
    };
  }
  return undefined;
};

const LocationStepPresenter: React.FC<LocationStepPresenterProps> = (
  props: LocationStepPresenterProps
) => {
  const [locationOptions, setLocationOptions] = useState<RadioCardOption[]>([]);
  const [location, setLocation] = useState<LocationDetails | undefined>(
    undefined
  );
  const [validate, setValidate] = useState<boolean>(false);

  useEffect(() => {
    const locationOptions = getLocationOptionsFromAppointmentType(
      props.schedulingDetails.providerDetails?.appointmentTypeList,
      props.schedulingDetails.appointmentInfo?.appointmentTypeId
    );
    setLocationOptions(locationOptions);
  }, [
    props.schedulingDetails.providerDetails?.appointmentTypeList,
    props.schedulingDetails.appointmentInfo?.appointmentTypeId,
  ]);

  useEffect(() => {
    if (
      location === undefined &&
      props.schedulingDetails.locationDetails?.searchLocationId
    ) {
      setLocation(
        findLocationDetails(
          locationOptions,
          props.schedulingDetails.locationDetails.searchLocationId
        )
      );
    } else if (location === undefined && locationOptions.length === 1) {
      // set the location to the first (and only) option
      // the auto continue
      const newLocation = findLocationDetails(
        locationOptions,
        locationOptions[0].value
      );
      setLocation(newLocation);
      fireContinue(newLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, props.schedulingDetails.locationDetails, locationOptions]);

  const onChangeLocation = (selectedValue: string) => {
    setLocation(findLocationDetails(locationOptions, selectedValue));
  };

  const fireContinue = (location: LocationDetails | undefined) => {
    if (isValid(location) && location) {
      props.onContinue(location);
    } else {
      setValidate(true);
    }
  };

  const onContinue = () => {
    fireContinue(location);
  };

  // return false to cancel onContinue
  const isValid = (location: LocationDetails | undefined): boolean => {
    return (
      location !== undefined &&
      location.searchLocationId !== undefined &&
      location.searchLocationId !== '' &&
      true
    );
  };

  return (
    <WizardStepWrapper
      onContinue={location !== undefined ? onContinue : undefined}
      stickyWhenNarrow={true}
    >
      <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
        <StackedColumn>
          <H2 text="Please select which location you would like to be seen at." />
          <IndicatesRequired />
        </StackedColumn>
        <RadioCardGroup
          label="Select location"
          required={true}
          validate={validate}
          groupName="selectLocation"
          sizing={RadioCardSizing.Large}
          orientation={RadioCardOptionOrientation.Wrap}
          radioOptions={locationOptions}
          selectedValue={location?.searchLocationId ?? ''}
          onChange={onChangeLocation}
        />
      </StackedColumn>
    </WizardStepWrapper>
  );
};

export default LocationStepPresenter;
